/**
 * Finding and following a tour step's target (SA-1315, spec §3). The port of the widget's
 * `ShowSession` (`widget/shared/showTarget.ts`): every layout read sits behind one
 * `TourGeometry` seam so the tests drive it (happy-dom has no layout). It imports no store.
 */
import { revealAction, type Box } from './tourModel'

export interface TourGeometry {
  /** The element's box, or null when it has no size (not rendered, `display: none`). */
  elementRect(el: Element): Box | null
  viewport(): { width: number; height: number }
  scrollIntoView(el: Element): void
  scrollBy(dy: number): void
  /** The bottom edge of a fixed or sticky element covering (x, y) other than `within` or its kin. */
  coveringFixedBottom(x: number, y: number, within: Element): number | null
  timer(cb: () => void, ms: number): () => void
  /** Anything that moves the page or `el`: scroll, resize, the element resizing. */
  observe(cb: () => void, el: Element): () => void
  /** Anything that changes the document, while waiting for a target to appear. */
  watchDom(cb: () => void): () => void
}

/** Room left between a covering header and the target. */
export const HEADER_MARGIN = 8
/** A reveal that rendered nothing is applied once more after this long (the widget's lesson). */
export const REVEAL_RETRY_MS = 400
/** How long a step waits for its target: on the same page, and after a route change. */
export const WAIT_MS = 3_000
export const WAIT_AFTER_ROUTE_MS = 5_000

export interface StepPlace {
  rect: Box
  /** Where a covering fixed or sticky header ends, 0 when nothing covers. */
  headerInset: number
}

export interface FollowOptions {
  doc: Document
  geo: TourGeometry
  anchor?: string
  /** A selector for the target in place of `anchor`'s, such as a nav row's (SA-1316). */
  selector?: string
  reveal?: string
  waitMs: number
  /** The target is on screen at `place`; called again as it moves. */
  onShown: (place: StepPlace, el: Element) => void
  /** The target did not come: point at `place` (the reveal anchor, else the page header), or nothing. */
  onMissing: (place: StepPlace | null) => void
}

export interface FollowHandle {
  dispose(): void
}

const ANCHOR_ATTR = 'data-tour'

function selector(id: string): string {
  return `[${ANCHOR_ATTR}="${id.replace(/["\\]/g, '')}"]`
}

/** The first element matching `sel` that has a size. */
function findSized(doc: Document, geo: TourGeometry, sel: string): Element | null {
  for (const el of Array.from(doc.querySelectorAll(sel))) {
    if (geo.elementRect(el)) return el
  }
  return null
}

/** The first element carrying `id` that has a size. */
export function findAnchor(doc: Document, geo: TourGeometry, id: string): Element | null {
  return findSized(doc, geo, selector(id))
}

function applyReveal(el: Element): void {
  const action = revealAction(el)
  if (action === 'details') (el as HTMLDetailsElement).open = true
  else if (action === 'tab') press(el as HTMLElement)
  else if (action) (el as HTMLElement).click()
}

/** The press a pointer makes (SA-1398): Radix tab triggers select on mousedown, never on a click
 * alone, and a tab that listens for clicks still gets one. A dispatched press moves no focus. */
function press(el: HTMLElement): void {
  for (const type of ['mousedown', 'mouseup']) {
    el.dispatchEvent(new MouseEvent(type, { bubbles: true, cancelable: true, button: 0 }))
  }
  el.click()
}

function headerInsetFor(geo: TourGeometry, rect: Box, el: Element): number {
  const cover = geo.coveringFixedBottom(rect.left + rect.width / 2, rect.top + 1, el)
  return cover !== null && cover > 0 ? cover : 0
}

export function followStep(opts: FollowOptions): FollowHandle {
  const { doc, geo } = opts
  const targetSel = opts.selector ?? selector(opts.anchor ?? '')
  const findTarget = () => findSized(doc, geo, targetSel)
  let disposed = false
  const cleanups: (() => void)[] = []
  const clear = () => {
    while (cleanups.length) cleanups.pop()!()
  }

  const show = (el: Element): void => {
    clear()
    geo.scrollIntoView(el)
    let rect = geo.elementRect(el)
    if (!rect) return wait()
    const cover = geo.coveringFixedBottom(rect.left + rect.width / 2, rect.top + 1, el)
    if (cover !== null && cover > rect.top) {
      geo.scrollBy(-(cover - rect.top + HEADER_MARGIN))
      rect = geo.elementRect(el) ?? rect
    }
    opts.onShown({ rect, headerInset: headerInsetFor(geo, rect, el) }, el)
    cleanups.push(
      geo.observe(() => {
        if (disposed) return
        const now = el.isConnected ? geo.elementRect(el) : null
        if (!now) return wait()
        opts.onShown({ rect: now, headerInset: headerInsetFor(geo, now, el) }, el)
      }, el)
    )
  }

  const missing = () => {
    clear()
    const fallback = (opts.reveal && findAnchor(doc, geo, opts.reveal)) || firstHeading(doc, geo)
    if (!fallback) {
      opts.onMissing(null)
    } else {
      geo.scrollIntoView(fallback)
      const rect = geo.elementRect(fallback)
      opts.onMissing(rect ? { rect, headerInset: headerInsetFor(geo, rect, fallback) } : null)
      cleanups.push(
        geo.observe(() => {
          const now = fallback.isConnected ? geo.elementRect(fallback) : null
          if (now) opts.onMissing({ rect: now, headerInset: headerInsetFor(geo, now, fallback) })
        }, fallback)
      )
    }
    // A target that turns up late is still shown.
    cleanups.push(
      geo.watchDom(() => {
        const el = findTarget()
        if (el && !disposed) show(el)
      })
    )
  }

  const tryReveal = () => {
    if (!opts.reveal) return
    const el = findAnchor(doc, geo, opts.reveal)
    if (el) applyReveal(el)
  }

  const wait = (): void => {
    clear()
    const now = findTarget()
    if (now) return show(now)
    tryReveal()
    cleanups.push(
      geo.watchDom(() => {
        const el = findTarget()
        if (el && !disposed) show(el)
      })
    )
    cleanups.push(geo.timer(() => !disposed && !findTarget() && tryReveal(), REVEAL_RETRY_MS))
    cleanups.push(geo.timer(() => !disposed && missing(), opts.waitMs))
  }

  wait()
  return {
    dispose() {
      disposed = true
      clear()
    },
  }
}

function firstHeading(doc: Document, geo: TourGeometry): Element | null {
  for (const el of Array.from(doc.querySelectorAll('main h1, h1'))) if (geo.elementRect(el)) return el
  return null
}

function nonEmpty(r: DOMRect | undefined): Box | null {
  if (!r || (r.width === 0 && r.height === 0)) return null
  return { top: r.top, left: r.left, width: r.width, height: r.height }
}

/** Marks the tour's own layers, which the header probe never takes for the page's header. */
export const TOUR_LAYER_ATTR = 'data-tour-layer'

function isTourLayer(el: Element): boolean {
  return !!el.closest(`[${TOUR_LAYER_ATTR}]`) || !!el.querySelector(`:scope > [${TOUR_LAYER_ATTR}]`)
}

/** The browser's geometry. Scrolling is instant: `auto` inherits a smooth `scroll-behavior`
 * and the rect would be read before the page moved (the widget's D8). */
export function defaultTourGeometry(doc: Document, win: Window): TourGeometry {
  const scroll = (fn: (behavior: ScrollBehavior) => void) => {
    try {
      fn('instant' as ScrollBehavior)
    } catch {
      try {
        fn('auto')
      } catch {
        // Something that cannot scroll is left where it is.
      }
    }
  }
  const frame = (cb: () => void) => {
    if (typeof win.requestAnimationFrame === 'function') win.requestAnimationFrame(() => cb())
    else win.setTimeout(cb, 16)
  }
  const throttled = (cb: () => void) => {
    let queued = false
    return () => {
      if (queued) return
      queued = true
      frame(() => {
        queued = false
        cb()
      })
    }
  }
  return {
    elementRect: (el) => nonEmpty(el.getBoundingClientRect()),
    viewport: () => ({ width: win.innerWidth, height: win.innerHeight }),
    scrollIntoView: (el) => scroll((behavior) => el.scrollIntoView({ block: 'center', inline: 'nearest', behavior })),
    scrollBy: (dy) => {
      if (Math.abs(dy) >= 1) scroll((behavior) => win.scrollBy({ top: dy, left: 0, behavior }))
    },
    coveringFixedBottom(x, y, within) {
      if (typeof doc.elementsFromPoint !== 'function') return null
      for (const el of doc.elementsFromPoint(x, y)) {
        if (within.contains(el) || el.contains(within) || isTourLayer(el)) continue
        const position = win.getComputedStyle(el).position
        if (position === 'fixed' || position === 'sticky') return el.getBoundingClientRect().bottom
      }
      return null
    },
    timer(cb, ms) {
      const id = win.setTimeout(cb, ms)
      return () => win.clearTimeout(id)
    },
    observe(cb, el) {
      const onMove = throttled(cb)
      win.addEventListener('scroll', onMove, { capture: true, passive: true })
      win.addEventListener('resize', onMove, { passive: true })
      const RO = (win as Window & { ResizeObserver?: typeof ResizeObserver }).ResizeObserver
      const ro = RO ? new RO(onMove) : null
      ro?.observe(el)
      return () => {
        win.removeEventListener('scroll', onMove, { capture: true } as EventListenerOptions)
        win.removeEventListener('resize', onMove)
        ro?.disconnect()
      }
    },
    watchDom(cb) {
      const MO = (win as Window & { MutationObserver?: typeof MutationObserver }).MutationObserver
      if (!MO) return () => {}
      const mo = new MO(throttled(cb))
      mo.observe(doc.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'hidden', 'open', 'data-tour', 'aria-expanded', 'aria-selected', 'data-state'],
      })
      return () => mo.disconnect()
    },
  }
}
